import { useState, useEffect, useRef, useCallback } from 'react'

export interface SimEntry {
  position:     number
  driverNumber: number
  driverName:   string
  speed:        number
  location:     string
  isDNF:        boolean
  lapProgress:  number   // total laps + fraction (0-200)
}

// Mirrors the fallback driver list so simulation uses real names
const SIM_DRIVERS = [
  { number: 2,  name: 'Josef Newgarden' },
  { number: 3,  name: 'Scott McLaughlin' },
  { number: 4,  name: 'Caio Collet' },
  { number: 5,  name: "Pato O'Ward" },
  { number: 6,  name: 'Helio Castroneves' },
  { number: 7,  name: 'Christian Lundgaard' },
  { number: 8,  name: 'Kyffin Simpson' },
  { number: 9,  name: 'Scott Dixon' },
  { number: 10, name: 'Alex Palou' },
  { number: 11, name: 'Katherine Legge' },
  { number: 12, name: 'David Malukas' },
  { number: 14, name: 'Santino Ferrucci' },
  { number: 15, name: 'Graham Rahal' },
  { number: 18, name: 'Romain Grosjean' },
  { number: 19, name: 'Dennis Hauger' },
  { number: 20, name: 'Alexander Rossi' },
  { number: 21, name: 'Christian Rasmussen' },
  { number: 23, name: 'Conor Daly' },
  { number: 24, name: 'Jack Harvey' },
  { number: 26, name: 'Will Power' },
  { number: 27, name: 'Kyle Kirkwood' },
  { number: 28, name: 'Marcus Ericsson' },
  { number: 31, name: 'Ryan Hunter-Reay' },
  { number: 33, name: 'Ed Carpenter' },
  { number: 45, name: 'Louis Foster' },
  { number: 47, name: 'Mick Schumacher' },
  { number: 51, name: 'Jacob Abel' },
  { number: 60, name: 'Felix Rosenqvist' },
  { number: 66, name: 'Marcus Armstrong' },
  { number: 75, name: 'Takuma Sato' },
  { number: 76, name: 'Rinus VeeKay' },
  { number: 77, name: 'Sting Ray Robb' },
]

const SECTIONS = [
  { t: 0.00, name: 'Start/Finish' },
  { t: 0.06, name: 'Front Straight' },
  { t: 0.16, name: 'Turn 1' },
  { t: 0.26, name: 'Short Chute 1' },
  { t: 0.34, name: 'Turn 2' },
  { t: 0.50, name: 'Back Straight' },
  { t: 0.66, name: 'Turn 3' },
  { t: 0.74, name: 'Short Chute 2' },
  { t: 0.84, name: 'Turn 4' },
  { t: 0.94, name: 'Front Straight' },
]

function lapFracToLocation(frac: number): string {
  const raw = frac % 1
  const t = raw === 0 ? 0 : 1 - raw   // reverse so cars go CW on screen
  for (let i = SECTIONS.length - 1; i >= 0; i--) {
    if (t >= SECTIONS[i].t) return SECTIONS[i].name
  }
  return 'Front Straight'
}

interface DriverState {
  number:      number
  name:        string
  lapProgress: number
  speed:       number
  isDNF:       boolean
}

export function useRaceSimulation(active: boolean) {
  const [entries, setEntries] = useState<SimEntry[]>([])
  const [lap, setLap] = useState(1)
  const stateRef = useRef<DriverState[]>([])

  // Spread drivers out around a mid-race starting point (lap 60-80)
  const init = useCallback(() => {
    const base = 68
    stateRef.current = SIM_DRIVERS.map((d, i) => ({
      number:      d.number,
      name:        d.name,
      lapProgress: base + (SIM_DRIVERS.length - i) * 0.09 + (d.number % 7) * 0.04,
      speed:       218 + (d.number % 13) * 1.4,
      isDNF:       false,
    }))
  }, [])

  const snapshot = useCallback(() => {
    const sorted = [...stateRef.current].sort((a, b) => {
      if (a.isDNF !== b.isDNF) return a.isDNF ? 1 : -1
      return b.lapProgress - a.lapProgress
    })
    const out: SimEntry[] = sorted.map((d, i) => ({
      position:     i + 1,
      driverNumber: d.number,
      driverName:   d.name,
      speed:        d.isDNF ? 0 : +d.speed.toFixed(1),
      location:     d.isDNF ? 'Pit Lane' : lapFracToLocation(d.lapProgress),
      isDNF:        d.isDNF,
      lapProgress:  d.lapProgress,
    }))
    setEntries(out)
    const leader = out.find(e => !e.isDNF)
    if (leader) setLap(Math.floor(leader.lapProgress))
  }, [])

  useEffect(() => {
    if (!active) return
    init()
    snapshot()

    const id = setInterval(() => {
      stateRef.current = stateRef.current.map(d => {
        if (d.isDNF) return d
        // ~2-3 DNFs per race, most likely in laps 30-160
        if (d.lapProgress > 30 && d.lapProgress < 160 && Math.random() < 0.002) {
          return { ...d, isDNF: true, speed: 0 }
        }
        // Speed wanders ±1 mph each tick
        const speed = Math.min(237, Math.max(212, d.speed + (Math.random() - 0.5) * 2))
        // Advance ~0.08 laps per 1200ms tick — gradual, realistic-feeling movement
        const advance = 0.07 + (Math.random() - 0.5) * 0.02
        return { ...d, speed, lapProgress: d.lapProgress + advance }
      })
      snapshot()
    }, 1200)

    return () => clearInterval(id)
  }, [active, init, snapshot])

  return { entries, lap }
}
